import {
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import {
  ArrowRightIcon,
  Bars3Icon,
  InformationCircleIcon,
  PlusIcon,
} from '@heroicons/react/20/solid';
import {
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ClockIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { InView } from 'react-intersection-observer';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { DeviceVersionBadge } from '@/components/UI/DeviceVersionBadge';
import { LoadingIcon } from '@/components/UI/LoadingIcon';
import { VipBadge, hasVipTag } from '@/components/UI/VipBadge';
import { CreateOrderDialog } from '@/pages/(guarded)/admin/(admin-tables)/orders/CreateOrderDialog';
import { ChannelIcons } from '@/pages/(guarded)/admin/DeliveryCoverage';
import Alert from '@/pages/(guarded)/release-center/components/Alert';
import { request } from '@/renderer/utils/api';
import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';
import { useVisualViewportHeight } from '@/renderer/utils/hooks/useVisualViewportHeight';
import { useLocale } from '@/renderer/utils/locale';
import { SafeTopSpacer } from '@/renderer/utils/safeTop';
// import { useIdleTimerContext } from 'react-idle-timer';
import useMe from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

import DM from './DM';
import MessageInput from './MessageInput';
import { MessageReceiptLogger } from './MessageReceiptLogger';
import SkeletonMessages from './SkeletonMessages';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Compact account age for the insights panel: "3.8 yrs" / "5 mo" / "12 days". */
function memberAge(iso?: string): string {
  if (!iso) {
    return '';
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days < 1) {
    return 'today';
  }
  if (days < 45) {
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  const months = Math.round(days / 30);
  if (months < 18) {
    return `${months} mo`;
  }
  return `${(days / 365).toFixed(1)} yrs`;
}

/** One account note, as returned by `/admin/user/:id/notes` (pinned-first,
 *  newest-first). Only the fields the info box renders are typed here. */
type AccountNote = {
  id: number | string;
  note: string;
  source: string | null;
  pinned: boolean;
  createdAt: string | null;
};

function StatCell({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  // Optional emphasis for the value (e.g. accent a non-zero promo-credit
  // balance so it doesn't read as just another muted number). Defaults to the
  // standard zinc value color.
  valueClassName?: string;
}) {
  return (
    <div className="bg-white px-4 py-2 dark:bg-zinc-800">
      <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
        {label}
      </div>
      <div className={valueClassName ?? 'font-semibold text-zinc-900 tabular-nums dark:text-white'}>
        {value}
      </div>
    </div>
  );
}

export default function DMReceiptList({ chatId = 'me' }: { chatId: API.Id | 'me' }) {
  // Size the support-chat layout to the visible viewport so the composer stays
  // above the on-screen keyboard on the Android WebView, which doesn't resize on
  // keyboard open (#1477). Drives `--support-vh`, consumed by SupportChat.less.
  useVisualViewportHeight();

  const me = useMe();
  const { urlParsed } = usePageContext();
  // The user-insights panel + admin link read admin-only endpoints, so gate the
  // whole thing on the admin role — a regular user must never see it, even if
  // they manually open another user's chat URL.
  const isAdmin = me?.role === 'admin';

  // Deep-link target from the DM list search (?m=<dmId>, #1802): the id of the
  // matched message to jump to. Parsed here — above the fetch — so the thread can
  // seed its FIRST page anchored on this message (a window of N before + N after
  // it) rather than the most-recent page, so a match older than the recent page
  // is actually loaded and can be scrolled to, with infinite scroll working in
  // both directions from the anchor (#1830).
  const targetDmId = (urlParsed.search as Record<string, string> | undefined)?.m;
  const targetDmIdNum =
    targetDmId && /^\d+$/.test(targetDmId) ? Number.parseInt(targetDmId, 10) : undefined;

  const getRequest = useMemo(
    () =>
      ({
        name: `admin.user.get:${chatId}`,
        input: `/api/v1/admin/search/users`,
      }) as RequestSchema<API.WithPage<API.User[]>>,
    [chatId],
  );

  const tableRequest = useNextInfiniteRequest(
    useMemo(
      () => ({
        key: getRequest.name as string,
        req: getRequest,
        // withChannels attaches the user's warning-channel reach (pushPlatforms +
        // hasTelegram) so the info card can show the full channels indicator (#1424).
        query: { data: { id: chatId, withChannels: true } },
        serverSkip: true,
        skipPoll: true,
        pause: !chatId || chatId === 'me' || !isAdmin,
        holdPrevious: false,
      }),
      [getRequest, chatId],
    ),
  );

  const user = tableRequest.data?.data?.[0];

  // Handy staff insights for the info panel: spend, orders, discounts, tenure.
  const ordersGetRequest = useMemo(
    () =>
      ({
        name: `admin.user.orders:${chatId}`,
        input: `/api/v1/admin/user/${chatId}/orders`,
      }) as RequestSchema<API.WithPage<any[]>>,
    [chatId],
  );

  const ordersRequest = useNextInfiniteRequest(
    useMemo(
      () => ({
        key: `admin.user.orders:${chatId}`,
        req: ordersGetRequest,
        query: { order: { by: 'createdAt', dir: 'DESC' }, page: { limit: 25 } },
        serverSkip: true,
        skipPoll: true,
        pause: !chatId || chatId === 'me' || !isAdmin,
        holdPrevious: false,
      }),
      [ordersGetRequest, chatId],
    ),
  );

  // Account notes (support/marketing) — the same admin read path the user
  // detail page uses (`/admin/user/:id/notes`): real `userNotes` rows merged
  // with any interim `metaPrivate.notes`, already sorted pinned-first then
  // newest-first. Gated on the admin role like the rest of this info box.
  const notesRequest = useRequest<{ notes: AccountNote[] }>(
    `/api/v1/admin/user/${chatId}/notes` as any,
    useMemo(() => ({ manual: !chatId || chatId === 'me' || !isAdmin }), [chatId, isAdmin]),
  );
  const notes: AccountNote[] = notesRequest.data?.notes ?? [];

  // Spendable promo credit (#1752) — the non-refundable, expiring promotional
  // credit pool, DISTINCT from the refundable `balance` column. A member can
  // hold a large promo balance on top of a small account balance (e.g. $75
  // balance + $400 promo credit); showing only `balance` led support to quote
  // the wrong spendable amount. The user object carries no promo total, so this
  // reads the same admin endpoint + canonical `spendable()` calc the admin user
  // page uses ({ total, grants } where total = SUM(remaining) over active,
  // non-revoked, non-expired credit grants).
  const promoRequest = useRequest<{ total: number }>(
    `/api/v1/admin/user/${chatId}/promo-credits` as any,
    useMemo(() => ({ manual: !chatId || chatId === 'me' || !isAdmin }), [chatId, isAdmin]),
  );
  const promoCredit = Number(promoRequest.data?.total ?? 0);
  const hasPromoCredit = promoCredit > 0;

  const orders: any[] = ordersRequest.data?.data ?? [];
  const totalSpent = orders.reduce((sum: number, o: any) => sum + (Number(o.amount) || 0), 0);
  const orderTotal = ordersRequest.data?.pageInfo?.total ?? orders.length;
  const discountedCount = orders.filter((o: any) => Number(o.discount) > 0).length;
  // Betting Talk is a separate product — noise for RAS picks support.
  const recentOrders = orders
    .filter((o: any) => !/betting talk/i.test(o.product?.title ?? ''))
    .slice(0, 3);

  const eventValidate = useCallback(
    async (
      _event: MessageEvent,
      ojson: API.SubscriptionResponse<API.DirectMessageReceipt> | API.WebsocketResult<API.Id>,
      knownIds: React.MutableRefObject<Set<number>>,
    ) => {
      const cId = chatId === 'me' ? me?.id : chatId;

      if (ojson.message === 'directMessageReceipt/created') {
        // We need to populate missing fields, as this event doesn't send references
        const json = ojson as API.SubscriptionResponse<API.DirectMessageReceipt>;

        if (!json.data.receiverUserId && json.data.userId !== cId) {
          return false; // not this chat, dont add (msg from user)
        } else if (json.data.receiverUserId && json.data.receiverUserId !== cId) {
          return false; // not this chat (msg from admin)
        }

        if (me && (!json.data.user || !json.data.receiverUser)) {
          if (json.data.userId === me.id) {
            json.data.user = me;
          }

          if (json.data.receiverUserId === me.id) {
            // Admin messaging themselves
            json.data.receiverUser = me;
          }

          return json;
        }
      } else if (
        ojson.message === 'directMessageReceipt/updated' &&
        knownIds.current.has(ojson.data as API.Id)
      ) {
        return {
          ...ojson,
          data: (
            await request<API.WithData<API.DirectMessageReceipt>, void, { type: 'dm' }>(
              `/api/v1/messageReceipt/${chatId}/${ojson.data as API.Id}`,
              { query: { type: 'dm' } },
            )
          ).data,
        } satisfies API.SubscriptionResponse<API.DirectMessageReceipt>;
      }
    },
    [me, chatId],
  );

  const isStaff = me?.role === 'staff' || me?.role === 'admin';

  const {
    isPending,
    isSuccess,
    error,
    data: results,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    hasPreviousPage,
    wsConnected,
    newestItem,
    oldestItem,
    data,
    renderPayload,
  } = useNextInfiniteRequest<
    API.DirectMessageReceipt,
    API.PageInput,
    API.WithPageInput<{ type: 'dm' }>
  >({
    key: `fetchDMs:${chatId}`,
    req: `/api/v1/messageReceipt/${chatId}`,
    event: 'directMessageReceipt',
    // skipPoll: true,
    query: {
      data: { type: 'dm' },
    },
    // When arriving from a search hit, seed the first page anchored on the matched
    // message (a window of messages before AND after it) so it is always loaded
    // and scrollable-to, then paginate outward in both directions (#1830). Live
    // updates / polling / incremental page loads use the plain `query` above, so
    // only this first fetch is anchored. Without a target this is undefined and
    // the thread opens at newest exactly as before.
    initialQuery:
      targetDmIdNum !== undefined
        ? { data: { type: 'dm' }, page: { around: targetDmIdNum } }
        : undefined,
    pause: chatId === 'me' && isStaff,
    eventValidate,
  });

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const loadOlderRef = useRef<HTMLButtonElement | null>(null);
  // Load-newer counterpart of loadOlderRef, for scrolling DOWN toward newer
  // messages when the thread was seeded anchored on an older search match (#1830).
  const loadNewerRef = useRef<HTMLButtonElement | null>(null);
  const scrollAnchorRef = useRef<{ id: number; top: number } | null>(null);
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  const [isAutoLoadingNewer, setIsAutoLoadingNewer] = useState(false);

  // useNextInfiniteRequest.isEmpty is false while still pending
  const isEmpty = !newestItem && !oldestItem;

  const doScroll = useCallback((selector: string, top?: boolean, scrollTopLimit?: number) => {
    const el = wrapRef.current?.querySelector(selector);

    if (top) {
      wrapRef.current?.scrollTo({ top: 1 });
    }

    el?.scrollIntoView?.({ block: 'start' });

    setTimeout(() => {
      const scrollTop = Math.abs(wrapRef.current?.scrollTop || 0);

      if (scrollTopLimit && scrollTop > scrollTopLimit) {
        // make sure user hasn't scrolled away; this prevents jitter
        return;
      }

      const el2 = wrapRef.current?.querySelector(selector);

      if (top) {
        wrapRef.current?.scrollTo({ top: -1 });
      }

      el2?.scrollIntoView?.({ block: 'start' });
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previousOldestId = useRef(oldestItem?.id);
  const previousNewestId = useRef(newestItem?.id);

  useEffect(() => {
    if (!wrapRef.current) {
      return;
    }

    const scrollTop = Math.abs(wrapRef.current.scrollTop);
    const newestChanged = !!newestItem?.id && newestItem.id !== previousNewestId.current;
    const oldestChanged = !!oldestItem?.id && oldestItem.id !== previousOldestId.current;

    // A load-older (fetchNextPage) or load-newer (fetchPreviousPage) page just
    // merged in and we saved an anchor beforehand. Restore the viewport so the
    // anchored row stays put rather than jumping to the newest message — this is
    // what keeps bidirectional infinite scroll from a search anchor stable, in
    // BOTH directions (older changes oldestItem, newer changes newestItem) (#1830).
    if (scrollAnchorRef.current && (oldestChanged || newestChanged)) {
      previousOldestId.current = oldestItem?.id;
      previousNewestId.current = newestItem?.id;

      const { id, top: savedTop } = scrollAnchorRef.current;
      const el = wrapRef.current.querySelector(`[data-dmid="${id}"]`);

      if (el) {
        const delta = el.getBoundingClientRect().top - savedTop;
        wrapRef.current.scrollTop += delta;
      }

      scrollAnchorRef.current = null;
      return;
    }

    if (newestChanged) {
      // New message at the bottom (live send/receive) or the very first page —
      // scroll to the end.
      previousNewestId.current = newestItem.id;
      // But also update oldest to prevent scroll on next check
      previousOldestId.current = oldestItem?.id;

      // ...unless newer rows are still unloaded (hasPreviousPage): then this
      // "newest" is only the newest of a window opened ANCHORED on an older search
      // match (#1830), not the conversation's newest, so jumping to it would yank
      // the thread off the match the deep-link effect is about to centre (and, with
      // the bottom sentinel then in view, kick off a load-newer). Just record the
      // ids and let the jump-to-match scroll own the viewport; the normal
      // scroll-to-end resumes once the user reaches the newest page.
      if (!hasPreviousPage) {
        doScroll(`[data-dmid="${parseInt(`${newestItem.id}`, 10)}"]`, true);
      }
    } else if (oldestChanged) {
      // Older messages loaded without a saved anchor — just record the new oldest.
      previousOldestId.current = oldestItem.id;
    } else if (scrollTop < 100 && scrollTop > 0 && newestItem?.id) {
      // Possibly an in-place update; keep pinned to the end if already near it.
      doScroll(`[data-dmid="${parseInt(`${newestItem.id}`, 10)}"]`, true, 250);
    }
  }, [oldestItem?.id, newestItem?.id, results, hasPreviousPage]);

  useEffect(() => {
    if (!isFetchingNextPage) {
      setIsAutoLoading(false);
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    if (!isFetchingPreviousPage) {
      setIsAutoLoadingNewer(false);
    }
  }, [isFetchingPreviousPage]);

  const intl = useLocale();

  // Snap the horizontal snap scroller to the DM pane when staff open a specific
  // conversation. A one-shot mount scroll is lost on a deep-link cold boot: the
  // DM data suspends and DMList lazy-loads, so it runs before the snap container
  // has its final width/children and the scroller rests on the first child (the
  // list) (#1566). Instead, re-run once this chat's data has resolved (keyed on
  // chatId + load completion) and defer to the next frame so the snap children
  // have settled. `scrolledForChatId` guards against re-snapping on every new
  // message, which would fight a user who scrolled back to the list.
  const scrolledForChatId = useRef<API.Id | 'me' | null>(null);
  useEffect(() => {
    if (!isStaff || chatId === 'me' || isPending) {
      return;
    }
    if (scrolledForChatId.current === chatId) {
      return;
    }
    scrolledForChatId.current = chatId;

    const raf = requestAnimationFrame(() => {
      document
        .getElementById('chat-content')
        ?.scrollIntoView({ behavior: 'instant', inline: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, [chatId, isStaff, isPending]);

  // Deep-link to a specific message when arriving from the DM list search
  // (?m=<dmId>, #1802/#1830). The first page is seeded ANCHORED on the target
  // (see initialQuery above), so once the thread has loaded the matched row is in
  // the window: scroll it to centre and mark it with a persistent highlight.
  // Guarded to fire once per (chat, target), and deferred past the newest-message
  // auto-scroll (doScroll retries near 500ms) so it lands on the match rather than
  // the bottom. `targetDmId` / `targetDmIdNum` are parsed at the top of the
  // component (above the fetch) so the anchor can seed the first page.
  const scrolledToTarget = useRef<string | null>(null);
  // The message row currently carrying the pale-yellow match highlight, so a new
  // target (or leaving the thread) can clear the previous one (#1815).
  const highlightedRef = useRef<HTMLElement | null>(null);
  // Gate the "load newer" affordance until the initial jump-to-match scroll has
  // settled — otherwise, on an anchored open, the bottom sentinel is briefly in
  // view and would auto-page newer before we centre the match (#1830). With no
  // target there is nothing to wait for, so it starts enabled.
  const [anchorSettled, setAnchorSettled] = useState(!targetDmIdNum);
  useEffect(() => {
    if (!targetDmId || isPending) {
      return;
    }
    const targetKey = `${chatId}:${targetDmId}`;
    if (scrolledToTarget.current === targetKey) {
      return;
    }
    const el = wrapRef.current?.querySelector<HTMLElement>(`[data-dmid="${targetDmId}"]`);
    if (!el) {
      // Not in the loaded window yet — leave the guard unset so a later page load
      // (results change) re-checks; if it never loads, the thread just opens at newest.
      return;
    }
    scrolledToTarget.current = targetKey;

    const timer = setTimeout(() => {
      // Jump INSTANTLY (not smooth): the match must be centred before we flip
      // `anchorSettled` below, or the load-newer sentinel renders while a smooth
      // scroll is still animating up from the window's bottom edge, sees itself in
      // view and pages newer — nudging the thread right back off the match (#1830).
      el.scrollIntoView({ block: 'center', behavior: 'auto' });
      // Persistent pale-yellow background on the matched message (#1815) — the same
      // idea as the gray hover state around a row, but kept in place so the found
      // message stays marked after the thread opens (not a brief flash). The hue
      // echoes the VIP badge's pale amber and reads in both light and dark. Set via
      // inline style (React never manages `style` on this row, so it survives
      // re-renders) and cleared off the previous target first.
      if (highlightedRef.current && highlightedRef.current !== el) {
        highlightedRef.current.style.backgroundColor = '';
      }
      el.style.transition = 'background-color 0.4s ease-out';
      el.style.backgroundColor = 'rgba(251, 191, 36, 0.16)';
      highlightedRef.current = el;
      // The match is now centred; it is safe to let the bottom sentinel drive
      // load-newer as the user scrolls down toward the newest messages (#1830).
      setAnchorSettled(true);
    }, 650);
    return () => clearTimeout(timer);
  }, [targetDmId, chatId, isPending, results]);

  // Clear the match highlight when the thread unmounts, so a reused row element
  // can't keep a stale pale-yellow background from a prior conversation.
  useEffect(
    () => () => {
      if (highlightedRef.current) {
        highlightedRef.current.style.backgroundColor = '';
        highlightedRef.current = null;
      }
    },
    [],
  );

  // const width = isStaff ? 'w-full' : 'w-full';

  const now = new Date();
  let currentHour = now.getUTCHours() - 5; // Convert to Central Time
  let currentDay = now.getUTCDay(); // 0 is Sunday, 6 is Saturday

  // Adjust for date rollover
  if (currentHour < 0) {
    currentHour += 24;
    currentDay -= 1;
    if (currentDay < 0) {
      currentDay = 6;
    }
  }

  // Check if it's between 9am and 5pm CT on a weekday
  const isBusinessHours =
    currentDay >= 1 && currentDay <= 5 && currentHour >= 9 && currentHour < 17;

  const isAfterHours = chatId === 'me' && !isBusinessHours;

  // Was the last message from me to admins in the last 30 mins?
  const isNewestRelativelyNewAndMine =
    !!newestItem?.createdAt &&
    newestItem.userId === me?.id &&
    Date.now() - Date.parse(newestItem.createdAt) < 1800000;

  // And did no admin talk to me within 15 mins of that message?
  const isNewestRelativelyNewAndMineButNoStaffMessagesRecently =
    isNewestRelativelyNewAndMine &&
    !data?.data
      .slice(0, 10)
      .find(
        (item) =>
          (item.user?.role === 'staff' || item.user?.role === 'admin') &&
          Date.parse(newestItem.createdAt) - Date.parse(item.createdAt) < 900000,
      );

  return (
    <>
      {/* Conversation header: this pane's own top chrome (not the shared
          renderer/Header, which the list pane renders), so it carries the iOS
          status-bar inset itself, the same way Header does — a spacer inside the
          bar that registers it as the inset owner, so the layout adds no second
          inset (renderer/utils/safeTop.tsx). The bar
          is in the fragment before any content, so the loading skeleton below
          it never starts under the Dynamic Island either (#2028). env() is 0 on
          web / Android. */}
      <div className="relative w-[100dvw] shrink-0 border-zinc-900/10 border-b p-2 shadow-xs md:w-auto md:px-4 dark:border-white/10">
        <SafeTopSpacer />
        <div className="flex flex-row items-center justify-between space-x-2">
          {isStaff ? (
            <label
              // Back-to-list control for the staff list↔conversation carousel. It is
              // shown whenever that carousel is active — i.e. below lg, matching the
              // pane breakpoint in DMPageWrapper — and hidden only at lg+ where the
              // list sits permanently beside the conversation (#1984).
              className="flex items-center justify-center rounded-sm p-1 hover:bg-zinc-900/5 lg:hidden dark:hover:bg-white/5"
              onClick={() => {
                document.getElementById('chat-list')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <ChevronLeftIcon
                height={20}
                className="h-5 w-auto shrink-0 cursor-pointer text-zinc-800 lg:hidden dark:text-zinc-300"
              />
            </label>
          ) : (
            <label
              htmlFor="nav-checkbox"
              className="flex items-center justify-center rounded-sm p-1 hover:bg-zinc-900/5 md:hidden dark:hover:bg-white/5"
            >
              <Bars3Icon
                height={20}
                className="m-auto size-5 shrink-0 text-zinc-800 md:hidden dark:text-zinc-300"
              />
            </label>
          )}

          <div className="flex w-full min-w-0 flex-row items-center px-2 md:space-x-4 md:px-0">
            <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
              <div className="flex min-w-0 items-center space-x-2">
                <div className="flex min-w-0 items-baseline justify-start space-x-2 overflow-hidden px-2">
                  <h2 className="m-0 min-w-0 select-none truncate p-0 font-semibold text-base text-zinc-800 dark:text-zinc-300">
                    {chatId === 'me'
                      ? 'Support Chat'
                      : user
                        ? `${user?.firstName}${
                            user?.lastName && user?.firstName && ' '
                          }${user?.lastName}`
                        : 'Loading...'}
                  </h2>
                  {/* Username is secondary and already shown in the info drawer,
                      so it's dropped from this cramped header on the narrowest
                      screens (shown from sm up). */}
                  {chatId !== 'me' && user && user?.id < 5680 && (
                    <span className="ml-2 hidden shrink-0 text-xs text-zinc-600 sm:inline dark:text-zinc-400">
                      {user?.username}
                    </span>
                  )}
                  {/* VIP flag (#1525) — high-value customer, tagged from order
                      history. Separate from the 'concierge' tag. */}
                  {chatId !== 'me' && hasVipTag(user) && (
                    <VipBadge className="shrink-0 self-center" />
                  )}
                  {/* Device app-version (#1530) — which build this user's device
                      is on; amber = behind the current web build. The verbose
                      build badge is hidden on the narrowest screens (shown from
                      sm up); it's mirrored in the info drawer below so nothing is
                      lost on mobile. */}
                  {chatId !== 'me' && (
                    <DeviceVersionBadge
                      devices={(user as any)?.deviceVersions}
                      className="hidden shrink-0 self-center sm:inline-flex"
                    />
                  )}
                </div>
                {chatId === 'me' || !isAdmin ? (
                  ''
                ) : (
                  <Popover className="inline-flex h-5 items-center">
                    <PopoverButton className="flex items-center rounded-sm outline-none">
                      <InformationCircleIcon
                        height={20}
                        className="size-5 stroke-1 text-zinc-400 dark:text-zinc-600"
                        aria-hidden="true"
                      />
                    </PopoverButton>

                    {/* Dim the page behind the sheet on mobile. No-op from md up,
                        where this is the small anchored dropdown (#2040). */}
                    <PopoverBackdrop
                      transition
                      className="fixed inset-0 z-40 bg-zinc-950/30 transition data-[closed]:opacity-0 md:hidden dark:bg-black/50"
                    />
                    <Transition
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-full md:translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-full md:translate-y-1"
                    >
                      {/* Mobile: a full-width bottom sheet — this panel is a dense
                          profile card, and anchored under the header it ran off the
                          bottom of a phone screen. From md up it stays the compact
                          anchored dropdown. `var(--safe-bottom)` keeps the last
                          action clear of the home indicator on iOS and of the
                          gesture bar on Android; it is 0 on web. (#2040, from
                          #1579; retargeted to the token in #2098.) */}
                      <PopoverPanel className="fixed inset-x-0 bottom-0 z-50 flex justify-center md:absolute md:inset-x-auto md:top-full md:bottom-auto md:left-2 md:z-30 md:mt-1 md:max-w-max md:justify-start lg:left-4">
                        <div className="max-h-[85dvh] w-full overflow-y-auto rounded-t-2xl bg-white pb-[var(--safe-bottom)] text-left text-sm text-zinc-800 shadow-2xl ring-1 ring-zinc-900/10 md:max-h-none md:w-80 md:max-w-[calc(100dvw-1rem)] md:overflow-hidden md:rounded-lg md:pb-0 md:shadow-lg dark:bg-zinc-800 dark:text-zinc-200 dark:ring-white/10">
                          {/* Grab handle — bottom-sheet affordance on mobile only. */}
                          <div className="flex justify-center pt-2.5 md:hidden">
                            <div className="h-1 w-9 rounded-full bg-zinc-300 dark:bg-zinc-600" />
                          </div>
                          {/* Identity + tenure */}
                          <div className="border-b border-zinc-900/5 px-4 py-3 dark:border-white/5">
                            <div className="font-semibold text-zinc-900 dark:text-white">
                              {user?.firstName} {user?.lastName}
                              {user?.role && user.role !== 'user' && (
                                <span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-zinc-500 dark:bg-zinc-700 dark:text-zinc-300">
                                  {user.role}
                                </span>
                              )}
                            </div>
                            <div className="mt-0.5 truncate text-xs text-zinc-500 dark:text-zinc-400">
                              @{user?.username}
                              {user?.email ? ` · ${user.email}` : ''}
                            </div>
                            {user?.createdAt && (
                              <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                                Member {memberAge(user.createdAt)} · joined{' '}
                                {intl.formatDate(user.createdAt, {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </div>
                            )}
                          </div>

                          {/* Stat grid. Order count moved out to the Recent
                              orders header (#1752); the freed slot now carries
                              spendable promo credit, kept next to Balance so
                              support can see the two distinct pools side by
                              side. */}
                          <div className="grid grid-cols-2 gap-px bg-zinc-900/5 dark:bg-white/5">
                            <StatCell label="Total spent" value={usd.format(totalSpent)} />
                            <StatCell
                              label="Discount"
                              value={
                                (user as any)?.discount != null &&
                                Number((user as any).discount) > 0
                                  ? `${(Number((user as any).discount) * 100).toFixed(0)}%`
                                  : '—'
                              }
                            />
                            <StatCell
                              label="Balance"
                              value={
                                (user as any)?.balance != null
                                  ? usd.format(Number((user as any).balance))
                                  : '—'
                              }
                            />
                            {/* Spendable promo credit — a non-zero balance is
                                accented (emerald) so it's obvious at a glance;
                                $0 renders quietly as an em-dash like the other
                                empty tiles. */}
                            <StatCell
                              label="Promo credit"
                              value={hasPromoCredit ? usd.format(promoCredit) : '—'}
                              valueClassName={
                                hasPromoCredit
                                  ? 'font-semibold text-emerald-600 tabular-nums dark:text-emerald-400'
                                  : undefined
                              }
                            />
                          </div>

                          {/* Warning-channel reach — the full channels indicator
                              (iOS / Android / Telegram + "none" badge) so support
                              sees every channel at a glance, not just Telegram (#1424). */}
                          <div className="flex items-center gap-2 border-t border-zinc-900/5 px-4 py-2 dark:border-white/5">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
                              Channels
                            </span>
                            {user ? (
                              <ChannelIcons user={user} />
                            ) : (
                              <span className="text-xs text-zinc-400 dark:text-zinc-500">—</span>
                            )}
                          </div>

                          {/* Device app-version (#1530) — mirrored here so the
                              build detail is reachable on mobile, where the
                              header badge is hidden to keep the row uncluttered. */}
                          {(user as any)?.deviceVersions?.length ? (
                            <div className="flex items-center gap-2 border-t border-zinc-900/5 px-4 py-2 dark:border-white/5">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
                                Device
                              </span>
                              <DeviceVersionBadge devices={(user as any).deviceVersions} />
                            </div>
                          ) : null}

                          {/* Account notes (support/marketing) — shown only
                              when the user has ≥1 note, so the box stays compact
                              for most users. Endpoint returns them pinned-first,
                              newest-first; the list scrolls if there are many so
                              a note-heavy account can't blow up the popover. */}
                          {notes.length > 0 && (
                            <div className="border-t border-zinc-900/5 px-4 py-2.5 dark:border-white/5">
                              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
                                Notes
                                <span className="ml-1 font-normal normal-case text-zinc-400 dark:text-zinc-500">
                                  · {notes.length}
                                </span>
                              </div>
                              <ul className="max-h-40 space-y-2 overflow-y-auto pr-1">
                                {notes.map((n) => (
                                  <li key={String(n.id)}>
                                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] text-zinc-400 dark:text-zinc-500">
                                      {n.pinned && (
                                        <span className="rounded bg-amber-50 px-1 py-0.5 font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                                          Pinned
                                        </span>
                                      )}
                                      {n.source && <span>{n.source}</span>}
                                      {n.createdAt && (
                                        <span>
                                          {intl.formatDate(n.createdAt, {
                                            month: 'short',
                                            day: 'numeric',
                                            year: '2-digit',
                                          })}
                                        </span>
                                      )}
                                    </div>
                                    <p className="mt-0.5 whitespace-pre-wrap text-xs text-zinc-700 dark:text-zinc-300">
                                      {n.note}
                                    </p>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Recent orders */}
                          <div className="px-4 py-2.5">
                            <div className="mb-1 flex items-baseline justify-between gap-2">
                              <div className="min-w-0 text-[10px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
                                Recent orders
                                {discountedCount > 0 && (
                                  <span className="ml-1 font-normal normal-case text-zinc-400 dark:text-zinc-500">
                                    · {discountedCount} discounted
                                  </span>
                                )}
                              </div>
                              {/* Total order count moved here from the stat grid
                                  (#1752), right-aligned against the header. */}
                              <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-zinc-400 tabular-nums dark:text-zinc-500">
                                {orderTotal} {orderTotal === 1 ? 'order' : 'orders'}
                              </span>
                            </div>
                            {recentOrders.length ? (
                              <ul className="space-y-1">
                                {recentOrders.map((o: any) => (
                                  <li
                                    key={o.id}
                                    className="flex items-baseline justify-between gap-2"
                                  >
                                    <span className="min-w-0 truncate text-zinc-700 dark:text-zinc-300">
                                      {o.product?.title ?? o.offer?.name ?? 'Order'}
                                      {Number(o.discount) > 0 && (
                                        <span className="ml-1 text-emerald-600 dark:text-emerald-400">
                                          −{o.discount}%
                                        </span>
                                      )}
                                    </span>
                                    <span className="shrink-0 tabular-nums text-zinc-500 dark:text-zinc-400">
                                      {o.amount != null ? usd.format(Number(o.amount)) : '—'}
                                      <span className="ml-1.5 text-zinc-400 dark:text-zinc-500">
                                        {o.createdAt
                                          ? intl.formatDate(o.createdAt, {
                                              month: 'short',
                                              day: 'numeric',
                                              year: '2-digit',
                                            })
                                          : ''}
                                      </span>
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="text-xs text-zinc-400 dark:text-zinc-500">
                                No orders yet
                              </div>
                            )}
                          </div>

                          {/* Footer link to full admin profile */}
                          <a
                            href={`/admin/users/${chatId}`}
                            className="flex items-center justify-center gap-1.5 border-t border-zinc-900/5 bg-zinc-50 p-2.5 font-semibold text-zinc-700 hover:bg-zinc-100 dark:border-white/5 dark:bg-zinc-900/40 dark:text-zinc-200 dark:hover:bg-zinc-900/70"
                          >
                            Open full profile in Admin
                          </a>
                        </div>
                      </PopoverPanel>
                    </Transition>
                  </Popover>
                )}
              </div>
              {chatId !== 'me' && isAdmin && (
                <div className="ml-auto flex shrink-0 items-center gap-1.5">
                  {user && (
                    <CreateOrderDialog
                      initialUser={{
                        id: user.id,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        username: user.username,
                        email: user.email,
                      }}
                      onSuccess={() => ordersRequest.refetch()}
                      triggerClassName="inline-flex items-center gap-1.5 rounded-md border border-zinc-950/10 px-2.5 py-1.5 font-medium text-xs text-zinc-700 transition hover:bg-zinc-100 dark:border-white/10 dark:text-zinc-300 dark:hover:bg-zinc-800"
                      triggerChildren={
                        <>
                          <PlusIcon className="size-4" />
                          <span className="hidden sm:inline">Create order</span>
                        </>
                      }
                    />
                  )}
                  <a
                    href={`/admin/users/${chatId}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-zinc-950/10 px-2.5 py-1.5 font-medium text-xs text-zinc-700 transition hover:bg-zinc-100 dark:border-white/10 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  >
                    <span className="hidden sm:inline">View in Admin</span>
                    <ArrowRightIcon className="size-4" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isAfterHours && (
        <Alert>
          <div className="flex flex-row items-start space-x-2 text-amber-700 text-sm dark:text-amber-200">
            <ClockIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
            <span>
              We're usually available during work hours on weekdays and around release times on the
              weekends. We'll get back to you as soon as possible.
            </span>
          </div>
        </Alert>
      )}
      {error && (
        <div className="bg-yellow-50 p-4 dark:bg-yellow-950/40">
          <div className="flex flex-row items-center space-x-2 text-sm text-yellow-700 dark:text-yellow-200">
            <ExclamationCircleIcon height={24} className="size-6" />
            <span>Error: {error.message}</span>
          </div>
        </div>
      )}
      {!isPending && ((!isSuccess && isEmpty) || !wsConnected) && (
        <>
          {!isSuccess && isEmpty && (
            <div className="bg-red-50 p-4 dark:bg-red-950/40">
              <div className="flex flex-row items-center space-x-2 text-red-700 text-sm dark:text-red-200">
                <ExclamationCircleIcon height={24} className="size-6" />
                <span>There seems to be a problem loading messages. We'll keep trying...</span>
              </div>
            </div>
          )}

          {/* {!isPending && !wsConnected && (
              <div className="bg-yellow-50 p-4">
                <div className="flex flex-row items-center space-x-2 text-sm text-yellow-700">
                  <SignalSlashIcon className="h-6 w-6" />
                  <span>Degraded connection. Messaging service may be delayed.</span>
                </div>
              </div>
            )} */}
        </>
      )}
      {isSuccess && isEmpty && !isStaff && !error && (
        <div className="flex h-full select-none flex-col items-center justify-center gap-5 px-8 text-center">
          <div className="flex size-16 items-center justify-center rounded-full bg-[#15734A]/10 text-[#15734A] dark:bg-[#34B473]/15 dark:text-[#34B473]">
            <ChatBubbleLeftRightIcon className="size-8 stroke-[1.5]" />
          </div>
          <h2 className="font-semibold text-xl text-zinc-900 dark:text-white">
            Have a question or comment?
          </h2>
          <p className="max-w-sm text-sm text-zinc-500 leading-6 dark:text-zinc-400">
            Start a conversation with us below and someone from the RAS team will get back to you as
            quickly as possible.
          </p>
        </div>
      )}
      {isPending && !(isStaff && chatId === 'me') && <SkeletonMessages />}
      {isEmpty && isStaff && chatId === 'me' && (
        <div className="flex h-full select-none flex-col items-center justify-center gap-3 px-6 text-center">
          <ChatBubbleLeftRightIcon className="size-12 stroke-1 text-zinc-300 dark:text-zinc-600" />
          <p className="max-w-xs text-sm text-zinc-500 leading-6 dark:text-zinc-400">
            Select a conversation from the sidebar to get started.
          </p>
        </div>
      )}
      {!isEmpty && !isPending && (
        <section
          className="flex h-fit grow flex-col-reverse overflow-y-auto overflow-x-hidden"
          style={{ overflowAnchor: 'none' }}
          ref={wrapRef}
        >
          <div className="flex flex-col pt-4">
            {hasNextPage && (
              <aside className="text-center">
                <InView
                  as="aside"
                  className="text-center"
                  rootMargin="600px 0px 0px 0px"
                  onChange={(inView, _entry) => {
                    if (
                      inView &&
                      isSuccess &&
                      !isFetching &&
                      !isFetchingNextPage &&
                      loadOlderRef.current
                    ) {
                      // Save the first visible element's position before loading
                      // so we can restore scroll position after new items render
                      const firstVisible = wrapRef.current?.querySelector(
                        '[data-dmid]',
                      ) as HTMLElement | null;

                      if (firstVisible && wrapRef.current) {
                        scrollAnchorRef.current = {
                          id: parseInt(firstVisible.dataset.dmid || '0', 10),
                          top: firstVisible.getBoundingClientRect().top,
                        };
                      }

                      setIsAutoLoading(true);
                      loadOlderRef.current.click();
                    }
                  }}
                ></InView>
                <button
                  disabled={isFetchingNextPage}
                  ref={loadOlderRef}
                  onClick={() => fetchNextPage()}
                  className="m-0 cursor-pointer select-none border-0 bg-transparent p-2 text-xs text-zinc-400 hover:text-zinc-500 dark:text-zinc-500 dark:hover:text-zinc-400"
                >
                  {isAutoLoading || isFetchingNextPage ? (
                    <LoadingIcon isLoading={true} className="h-4 w-4 opacity-50" />
                  ) : (
                    'Load older messages'
                  )}
                </button>
              </aside>
            )}

            {renderPayload((payload) => {
              const receipts = [...payload.data].reverse();

              return receipts.map((props, receiptIndex) => {
                const receiptTime = Date.parse(props.createdAt);
                const receiptSenderId = props.userId;
                const olderReceipt = receipts[receiptIndex - 1];

                const date = intl.formatDate(props.createdAt, {
                  month: 'long',
                  day: 'numeric',
                  weekday: 'short',
                });

                let showDate = true;
                let showUserInfo = true;

                if (olderReceipt) {
                  const olderReceiptSenderId = olderReceipt.userId;
                  const olderReceiptTime = Date.parse(olderReceipt.createdAt);
                  const olderDate =
                    !!olderReceiptTime &&
                    intl.formatDate(olderReceipt.createdAt, {
                      month: 'long',
                      day: 'numeric',
                      weekday: 'short',
                    });

                  if (olderDate === date) {
                    showDate = false;

                    if (
                      olderReceipt.receiverUserId === props.receiverUserId &&
                      receiptTime - olderReceiptTime <= 5 * 60 * 1000 &&
                      receiptSenderId === olderReceiptSenderId
                    ) {
                      showUserInfo = false;
                    }
                  }
                }

                return (
                  <React.Fragment key={props.id}>
                    {!!showDate && (
                      <div className="my-4 flex items-center px-4">
                        <hr className="h-px grow border-0 bg-zinc-900/10 dark:bg-white/10" />
                        <time className="shrink-0 px-2 text-xs text-zinc-900/50 dark:text-zinc-500">
                          {intl.formatDate(props.createdAt, {
                            month: 'long',
                            day: 'numeric',
                            weekday: 'short',
                          })}
                        </time>
                        <hr className="h-px grow border-0 bg-zinc-900/10 dark:bg-white/10" />
                      </div>
                    )}

                    <DM receipt={props} userInfo={showUserInfo} />

                    {(props.receiverUserId === me?.id || (!props.receiverUserId && isStaff)) && (
                      <ErrorBoundary>
                        <Suspense>
                          <MessageReceiptLogger {...props} />
                        </Suspense>
                      </ErrorBoundary>
                    )}
                  </React.Fragment>
                );
              });
            })}
            {!isStaff && isNewestRelativelyNewAndMineButNoStaffMessagesRecently && isAfterHours && (
              <DM
                receipt={
                  {
                    id: 0,
                    content:
                      "Thanks for writing to us. You're reaching us outside of our normal business hours. Your message has been received and we will get back to you as soon as we can.",
                    receiverUserId: -1,
                    receiverUser: {
                      //
                    },
                    userId: 0,
                    user: {
                      id: 0,
                      role: 'staff',
                      firstName: 'RAS',
                    },
                    visible: true,
                  } as API.DirectMessageReceipt
                }
                userInfo={true}
              />
            )}
            {hasPreviousPage && anchorSettled && (
              <aside className="text-center">
                <InView
                  as="aside"
                  className="text-center"
                  rootMargin="0px 0px 600px 0px"
                  onChange={(inView, _entry) => {
                    if (
                      inView &&
                      isSuccess &&
                      !isFetching &&
                      !isFetchingPreviousPage &&
                      loadNewerRef.current
                    ) {
                      // Anchor on the current newest row so the viewport stays put
                      // when the newer page renders just below it (#1830).
                      const anchorEl = wrapRef.current?.querySelector(
                        `[data-dmid="${newestItem?.id}"]`,
                      ) as HTMLElement | null;

                      if (anchorEl && newestItem?.id && wrapRef.current) {
                        scrollAnchorRef.current = {
                          id: parseInt(`${newestItem.id}`, 10),
                          top: anchorEl.getBoundingClientRect().top,
                        };
                      }

                      setIsAutoLoadingNewer(true);
                      loadNewerRef.current.click();
                    }
                  }}
                ></InView>
                <button
                  disabled={isFetchingPreviousPage}
                  ref={loadNewerRef}
                  onClick={() => fetchPreviousPage()}
                  className="m-0 cursor-pointer select-none border-0 bg-transparent p-2 text-xs text-zinc-400 hover:text-zinc-500 dark:text-zinc-500 dark:hover:text-zinc-400"
                >
                  {isAutoLoadingNewer || isFetchingPreviousPage ? (
                    <LoadingIcon isLoading={true} className="h-4 w-4 opacity-50" />
                  ) : (
                    'Load newer messages'
                  )}
                </button>
              </aside>
            )}
          </div>
        </section>
      )}
      {(!isStaff || (chatId !== 'me' && chatId !== 1647)) && (
        <Suspense>
          <MessageInput user={user} chatId={chatId} />
        </Suspense>
      )}
    </>
  );
}
